import Header from '@/components/Header/Header';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useGetTenant,
  useListOrganizations,
  useListPayersForTenant,
} from '@/api/trafikportalen';
import { Loading } from '@/components/Loading.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { createSortConfig } from '@/utils/sort';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { FilterPanel } from '@/components/FilterPanel';
import Input from '@/components/core/Input';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import { VirtualizedList } from '@/components/VirtualizedList';
import {
  PayerListItemData,
  PayerListItem,
} from '@/components/Organizations/PayerListItem';
import {
  FilterType,
  SearchFilterParams,
  SortDirection,
} from '@/types/filterSearch';
import { useAscOrDescIcon } from '@/hooks/useSort';
import Button from '@/components/core/Button';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { DocumentHead } from '@/components/core/DocumentHead';
import { isOrganizationInactiveBeyondPayerListThreshold } from '@/helpers/organizationStatus';

export const Route = createFileRoute('/$tenantId/_layout/payers/')({
  component: Payers,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      filter: search.filter as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
    };
  },
  beforeLoad: ({ params }) => {
    const hasPermission = checkUserPermissions({
      permission: 'organization:admin',
    });
    if (!hasPermission) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function Payers() {
  const { data: tenant, isLoading: isLoadingTenant } = useGetTenant();
  const { data: organizations, isLoading: isLoadingOrganizations } =
    useListOrganizations();
  const { data: payersRaw, isLoading: isLoadingPayers } =
    useListPayersForTenant();
  const { formatMessage } = useIntl();
  const getIcon = useAscOrDescIcon();
  const { query, filter: filterPayer, sort, direction } = Route.useSearch();
  const rightPanelRef = useRef<HTMLElement>(null);
  const [announcement, setAnnouncement] = useState('');

  const startOption = useMemo(
    (): FilterType => ({
      id: 'all',
      label: formatMessage({ defaultMessage: 'Alla' }),
    }),
    [formatMessage],
  );

  const statusOptions = useMemo(() => {
    return [
      startOption,
      {
        id: 'incomplete',
        label: formatMessage({ defaultMessage: 'Måste kompletteras' }),
      },
      { id: 'complete', label: formatMessage({ defaultMessage: 'Godkänd' }) },
    ];
  }, [formatMessage, startOption]);

  const payers = useMemo<PayerListItemData[]>(() => {
    if (!tenant || !organizations || !payersRaw) return [];

    return payersRaw.reduce((items, payer) => {
      const organization = organizations.find(
        (org) => org.id === payer.organization_id,
      );

      if (!organization) return items;

      if (isOrganizationInactiveBeyondPayerListThreshold(organization)) {
        return items;
      }

      const requiredProps = organization.is_internal
        ? tenant?.required_internal_invoicing_details
        : tenant?.required_external_invoicing_details;

      const missingProps = requiredProps?.filter(
        (prop) => !payer.details[prop],
      );

      return [
        ...items,
        {
          id: payer.id,
          orgName: organization?.name ?? '',
          orgId: organization?.id ?? '',
          status: missingProps?.length > 0 ? 'incomplete' : 'complete',
          detailsString: Object.values(payer.details)
            .filter(Boolean)
            .join(', '),
          isInternal: organization?.is_internal ?? false,
          createdAt: payer.created_at,
          updatedAt: payer.updated_at,
        },
      ];
    }, [] as PayerListItemData[]);
  }, [organizations, tenant, payersRaw]);

  const sortConfigs = useMemo(() => {
    return createSortConfig<PayerListItemData>({
      textFields: ['createdAt', 'updatedAt'],
    });
  }, []);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<PayerListItemData>({
    items: payers || [],
    searchKeys: ['orgName', 'detailsString'],
    sortConfigs,
    routePath: Route.fullPath,
    defaultSearchParams: {
      sort: 'createdAt',
      direction: 'desc',
      filter: startOption.id,
    },
  });

  const filteredPayers = useMemo(() => {
    if (!sortedItems || !filterSearch?.filter) {
      return [];
    }

    if (filterSearch.filter.id === startOption.id) {
      return sortedItems;
    }

    return sortedItems.filter(
      (payer) => payer.status === filterSearch.filter?.id,
    );
  }, [filterSearch?.filter, sortedItems, startOption.id]);

  const handleFilter = useCallback(
    (filter: OptionsType) => {
      setFilterSearch((prev) => ({
        ...prev!,
        filter: filter === prev?.filter ? startOption : (filter as FilterType),
      }));
      handleSearchParams({
        filter: filter.id as string,
      });
    },
    [handleSearchParams, setFilterSearch, startOption],
  );

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredPayers.length;
    const sortLabel =
      filterSearch.sort === 'createdAt'
        ? formatMessage({ defaultMessage: 'datum skapad' })
        : formatMessage({ defaultMessage: 'datum uppdaterad' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {fakturamottagare} other {fakturamottagare}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredPayers.length, formatMessage]);

  useEffect(() => {
    if (filterSearch) return;

    const status = filterPayer
      ? ((statusOptions.find((d) => d.id === filterPayer) as FilterType) ??
        startOption)
      : startOption;

    setFilterSearch({
      query: query ?? '',
      filter: status,
      sort: sort ?? 'createdAt',
      direction: direction ?? 'desc',
    });
  }, [
    direction,
    filterPayer,
    query,
    sort,
    filterSearch,
    setFilterSearch,
    statusOptions,
    startOption,
  ]);

  const renderPayerListItem = useCallback((payer: PayerListItemData) => {
    return <PayerListItem key={payer.id} {...payer} />;
  }, []);

  if (isLoadingTenant || isLoadingOrganizations || isLoadingPayers) {
    return <Loading />;
  }

  return (
    <>
      <DocumentHead
        titleKey="page.payers.title"
        descriptionKey="page.payers.description"
      />
      <Header title={<FormattedMessage defaultMessage="Fakturamottagare" />} />
      <FilteredListContainer>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <RightPanel ref={rightPanelRef}>
          {filterSearch && (
            <FilterPanel
              firstInput={
                <div className="flex w-full flex-col gap-8">
                  <Input
                    type="text"
                    label={formatMessage({ defaultMessage: 'Sök' })}
                    placeholder={formatMessage({
                      defaultMessage: 'Sök org eller detaljer',
                    })}
                    id="payer-search"
                    value={filterSearch.query}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              }
              filterOptions={[
                <Dropdown
                  key={statusOptions.length}
                  label={formatMessage({ defaultMessage: 'Status' })}
                  id="payer-status"
                  options={statusOptions}
                  placeholder={formatMessage({ defaultMessage: 'Status' })}
                  value={filterSearch.filter}
                  onValueChange={(value) => handleFilter(value)}
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera fakturamottagare genom att välja status',
                  })}
                  containerRef={rightPanelRef}
                />,
              ]}
              sortingOptions={[
                <Button
                  variant={
                    filterSearch.sort! === 'createdAt' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'createdAt',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('createdAt')}
                  aria-label={`Sortera efter skapad den ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'createdAt'}
                >
                  <FormattedMessage defaultMessage="Skapad" />
                </Button>,
                <Button
                  variant={
                    filterSearch.sort! === 'updatedAt' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'updatedAt',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('updatedAt')}
                  aria-label={`Sortera efter uppdaterad den ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'updatedAt'}
                >
                  <FormattedMessage defaultMessage="Uppdaterad" />
                </Button>,
              ]}
            />
          )}
        </RightPanel>
        <VirtualizedList
          count={filteredPayers.length}
          countName="fakturamottagare"
          totalItems={payers}
          filteredItems={filteredPayers}
          renderItem={renderPayerListItem}
          getItemKey={(payer) => payer.id}
        />
      </FilteredListContainer>
    </>
  );
}
